import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import StyleSettings from './EmbedStyleSettings.jsx';
import { ItemRow, Detail, Sections, Metrics, Glance, EditLayout, PreviewPane, FormPreview, SaveBar, moved, when } from './website/HubKit.jsx';

// Newsletter sign-up forms — a "join our mailing list" box for the client's OWN
// website whose sign-ups land in Pulse (server/newsletterForms.js).
//
// Dual-surface via `scope`:
//   'admin-client' → Admin → the client → Newsletter sign-ups
//   'my'           → Settings → Newsletter sign-ups
//
// Three ways onto a site, all shown under "Add it to your website": the
// one-line web component, a hosted link (bios, QR codes, an iframe) and the
// JSON endpoint for a developer styling their own form. The sign-ups are then a
// live audience in Engage → Segments ("Newsletter sign-ups").
//
// Mobile-first: one stacked column, ≥40px controls, tables scroll inside
// themselves rather than pushing the page sideways.
const BLANK = {
  name: '', defaultLang: 'en', translations: {}, popup: { delay: 8, scroll: 0, exit: true, days: 7 }, heading: '', showHeading: true, doubleOptIn: true, confirmSubject: '', confirmBody: '', confirmButton: '', confirmedMessage: '', blurb: '', buttonLabel: '', successMessage: '', privacyNotice: '', privacyUrl: '', labelStyle: 'inside', brandColor: '', allowedDomains: '',
  fields: [{ key: 'first_name', label: 'First name', type: 'text', required: false, options: [] }, { key: 'last_name', label: 'Last name', type: 'text', required: false, options: [] }],
  consents: [{ key: 'email', channel: 'email', title: 'Newsletters', text: 'We will send you occasional emails about events, line-ups and ticket releases. You can unsubscribe at any time.', required: true }],
};
// Field presets offered by "+ Add field". The built-in keys mean something to
// Pulse (name, mobile → SMS, country → a filter); custom ones become answers.
const FIELD_PRESETS = [
  { key: 'first_name', label: 'First name', type: 'text' },
  { key: 'last_name', label: 'Last name', type: 'text' },
  { key: 'name', label: 'Full name', type: 'text' },
  { key: 'mobile', label: 'Mobile', type: 'tel' },
  { key: 'country', label: 'Country', type: 'select' },
  { key: '', label: 'Region', type: 'text', custom: true },
  { key: '', label: 'Custom text', type: 'text', custom: true },
  { key: '', label: 'Custom dropdown', type: 'select', custom: true },
];
const CONSENT_PRESETS = [
  { channel: 'email', title: 'Newsletters', text: 'We will send you occasional emails about events, line-ups and ticket releases. You can unsubscribe at any time.' },
  { channel: 'sms', title: 'SMS updates', text: 'We may text you about events and ticket releases. Reply STOP to opt out.' },
  { channel: 'ads', title: 'Customized online advertising', text: 'We will use your information to show you ads that are more relevant to you.' },
  { channel: 'other', title: 'Terms', text: 'I agree to the terms and conditions.' },
];
const CHANNEL_LABEL = { email: 'Email list', sms: 'SMS list', ads: 'Ad audiences', other: 'Agreement only' };

export default function NewsletterFormsCard({ entityId, scope = 'my' }) {
  const apiScope = scope === 'my' ? 'my' : 'admin';
  const [forms, setForms] = useState(null);
  const [denied, setDenied] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  // Where we are: the list, one form (and which view), or a new form.
  const [view, setView] = useState({ mode: 'list', id: '', tab: 'overview' });
  const [draft, setDraft] = useState(BLANK);
  const [q, setQ] = useState('');

  const load = useCallback(() => api.newsletterForms(entityId, apiScope)
    .then((v) => { if (v && v.error) throw new Error(v.error); setForms(v.forms || []); })
    .catch(() => setDenied(true)), [entityId, apiScope]);
  useEffect(() => { load(); }, [load]);

  if (denied) return null; // flag off / no permission — the section simply isn't there
  if (!forms) return <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Loading sign-up forms…</div>;

  const run = async (key, fn) => {
    setBusy(key); setErr('');
    try {
      const v = await fn();
      if (v && v.error) { setErr(v.error); return false; }
      if (v && v.forms) setForms(v.forms);
      return v || true;
    } catch (e) { setErr(String(e.message || e)); return false; }
    finally { setBusy(''); }
  };
  const open = (f, tab = 'overview') => { setDraft(toDraft(f)); setErr(''); setView({ mode: 'item', id: f.id, tab }); };
  const startNew = () => { setDraft(BLANK); setErr(''); setView({ mode: 'new', id: '', tab: 'edit' }); };
  const back = () => { setErr(''); setView({ mode: 'list', id: '', tab: 'overview' }); };
  const save = async () => {
    const r = await run('save', () => (view.mode === 'new'
      ? api.newsletterFormCreate(entityId, apiScope, { ...draft })
      : api.newsletterFormUpdate(entityId, apiScope, view.id, { ...draft })));
    if (!r) return;
    const id = view.mode === 'new' ? r.id : view.id;
    const f = (r.forms || []).find((x) => x.id === id);
    if (f) open(f, 'overview');
  };

  // ── the list ───────────────────────────────────────────────────────────────
  if (view.mode === 'list') {
    const needle = q.trim().toLowerCase();
    const shown = forms.filter((f) => !needle || f.name.toLowerCase().includes(needle));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ ...hint, margin: 0 }}>
          Mailing-list sign-up forms for <b>your own website</b>. Everyone who signs up lands in Pulse, in a segment of their own
          (Engage → Segments, Newsletter folder), ready for your next campaign.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {forms.length > 3 && <input type="search" style={{ ...input, flex: '1 1 240px' }} placeholder="Search sign-up forms" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search sign-up forms" />}
          <button type="button" style={{ ...btn, ...primary }} onClick={startNew}>+ New sign-up form</button>
        </div>
        {forms.length === 0 && <div style={okBox}>No sign-up forms yet. Create one, then add it to your website.</div>}
        {shown.map((f) => (
          <ItemRow key={f.id} name={f.name} status={f.status === 'paused' ? '⏸ Paused' : '● Live'} tone={f.status === 'paused' ? 'paused' : 'live'}
            badges={f.origin === 'popup' ? ['Used by a website pop-up'] : []}
            summary={`${['Email', ...(f.fields || []).map((x) => x.label)].join(' · ')} · ${(f.consents || []).length} consent box${(f.consents || []).length === 1 ? '' : 'es'}${f.doubleOptIn ? ' · double opt-in' : ''}`}
            stats={[{ value: f.total.toLocaleString(), label: 'sign-ups' }, { value: f.lastWeek.toLocaleString(), label: 'this week' }, { value: when(f.lastSignup), label: 'latest' }]}
            onOpen={() => open(f)} />
        ))}
        {err && <div style={errBox}>{err}</div>}
      </div>
    );
  }

  // ── one form (or a new one) ───────────────────────────────────────────────
  const f = forms.find((x) => x.id === view.id);
  const isNew = view.mode === 'new';
  if (!isNew && !f) { back(); return null; }
  const editor = (
    <EditLayout
      editor={(
        <>
          <Editor draft={draft} setDraft={setDraft} entityId={entityId} apiScope={apiScope} isNew={isNew} />
          <SaveBar onSave={save} onCancel={isNew ? back : () => open(f, 'overview')} busy={busy === 'save'} isNew={isNew} createLabel="Create form" error={err} />
        </>
      )}
      preview={<SignupPreviewPane draft={draft} formId={isNew ? '' : f.id} entityId={entityId} apiScope={apiScope} />}
    />
  );
  if (isNew) return <Detail backLabel="Newsletter sign-ups" onBack={back} name={draft.name || 'New sign-up form'}>{editor}</Detail>;
  return (
    <Detail backLabel="Newsletter sign-ups" onBack={back} name={f.name} status={f.status === 'paused' ? '⏸ Paused' : '● Live'} tone={f.status === 'paused' ? 'paused' : 'live'}
      actions={[
        { label: f.status === 'paused' ? 'Resume' : 'Pause', disabled: !!busy, onClick: () => run(`st-${f.id}`, () => api.newsletterFormUpdate(entityId, apiScope, f.id, { status: f.status === 'paused' ? 'active' : 'paused' })) },
        { label: 'Delete', danger: true, disabled: !!busy, onClick: async () => { if (window.confirm(`Delete “${f.name}” and its ${f.total} sign-up${f.total === 1 ? '' : 's'}? Download the list first if you need it. Websites using this form will stop accepting sign-ups.`) && await run(`rm-${f.id}`, () => api.newsletterFormRemove(entityId, apiScope, f.id))) back(); } },
      ]}
      tabs={[['overview', 'Overview'], ['people', 'Sign-ups'], ['code', 'Add to website'], ['edit', 'Edit']]}
      tab={view.tab} onTab={(tab) => { if (tab === 'edit') setDraft(toDraft(f)); setView({ ...view, tab }); }}>
      {view.tab === 'overview' && <Overview f={f} entityId={entityId} apiScope={apiScope} />}
      {view.tab === 'people' && <PeoplePanel form={f} entityId={entityId} apiScope={apiScope} />}
      {view.tab === 'code' && <CodePanel form={f} />}
      {view.tab === 'edit' && editor}
      {view.tab !== 'edit' && err && <div style={errBox}>{err}</div>}
    </Detail>
  );
}

const toDraft = (f) => ({ ...BLANK, ...f, allowedDomains: (f.allowedDomains || []).join(', '), fields: f.fields || [], consents: f.consents || [] });

function Overview({ f, entityId, apiScope }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Metrics items={[
        { value: f.total.toLocaleString(), label: 'Sign-ups', hint: f.doubleOptIn ? 'Confirmed, in the segment' : 'In the segment' },
        { value: f.lastWeek.toLocaleString(), label: 'Last 7 days' },
        f.doubleOptIn && { value: (f.pending || 0).toLocaleString(), label: 'Waiting to confirm', hint: 'Deleted after 30 days' },
        { value: when(f.lastSignup), label: 'Latest sign-up' },
      ]} />
      <Glance rows={[
        ['Fields', ['Email', ...(f.fields || []).map((x) => `${x.label}${x.required ? ' *' : ''}`)].join(' · ')],
        ['Consent boxes', (f.consents || []).map((c) => `${c.title || c.channel} (${CHANNEL_LABEL[c.channel] || c.channel})`).join(' · ')],
        ['After they sign up', f.doubleOptIn ? 'Confirmation email first (double opt-in)' : 'Joins the list straight away'],
        ['Segment', f.segmentId ? `Engage → Segments, ${f.origin === 'popup' ? 'pop-up folder' : '📰 Newsletter folder'}${f.adsSegmentId ? ' · plus an ad-consent segment' : ''}` : 'Created on first save'],
        ['Popup', popupWhen(f.popup)],
        ['Languages', [LANGS[f.defaultLang] || f.defaultLang, ...Object.keys(f.translations || {}).map((l) => LANGS[l] || l)].join(', ')],
        ['Only from these websites', (f.allowedDomains || []).join(', ') || 'Any website'],
      ]} />
      <SignupPreviewPane draft={toDraft(f)} formId={f.id} entityId={entityId} apiScope={apiScope} />
    </div>
  );
}

function SignupPreviewPane({ draft, formId, entityId, apiScope }) {
  const langs = [draft.defaultLang || 'en', ...Object.keys(draft.translations || {})];
  const [lang, setLang] = useState(langs[0]);
  const cur = langs.includes(lang) ? lang : langs[0];
  return (
    <PreviewPane langs={langs.map((l) => [l, LANGS[l] || l])} lang={cur} onLang={setLang}
      note="Drawn by the same code your website runs. Signing up here only shows the thank-you; nobody is added.">
      <FormPreview kind="newsletter" entityId={entityId} apiScope={apiScope} form={draft} formId={formId} lang={cur} />
    </PreviewPane>
  );
}

// The editor: every setting, in collapsible sections that each say what's set.
function Editor({ draft, setDraft, isNew, entityId, apiScope }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const text = (k, lbl, ph, opts = {}) => (
    <label style={label}>{lbl}
      {opts.area
        ? <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={draft[k] || ''} placeholder={ph} maxLength={opts.max} onChange={(e) => set(k, e.target.value)} />
        : <input style={input} value={draft[k] || ''} placeholder={ph} maxLength={opts.max} onChange={(e) => set(k, e.target.value)} />}
    </label>
  );
  const fields = draft.fields || [];
  const consents = draft.consents || [];
  const langs = Object.keys(draft.translations || {});
  const st = draft.style || {};
  return (
    <Sections initial={isNew ? ['basics', 'fields'] : []} list={[
      { id: 'basics', title: 'Name, heading & button', summary: `${draft.name || 'Untitled'} · ${draft.showHeading === false ? 'no heading' : `“${draft.heading || 'Join our mailing list'}”`} · button “${draft.buttonLabel || 'Sign up'}”`, content: (
        <>
          {text('name', 'Name (only you see this)', 'e.g. Website footer', { max: 120 })}
          <label style={{ ...label, ...chkRow }}><input type="checkbox" style={chk} checked={draft.showHeading !== false} onChange={(e) => set('showHeading', e.target.checked)} /> Show a heading (untick if your page already has its own title)</label>
          {draft.showHeading !== false && text('heading', 'Heading', 'Join our mailing list', { max: 160 })}
          {text('blurb', 'Intro line', 'Be first to hear about new events, line-ups and ticket releases.', { area: true, max: 600 })}
          {text('buttonLabel', 'Button', 'Sign up', { max: 40 })}
        </>
      ) },
      { id: 'fields', title: 'Form layout', summary: ['Email', ...fields.map((x) => x.label || '…')].join(' · '), content: (
        <>
          <div style={{ ...hint, marginTop: 8 }}>Email is always asked, after the name fields. Move the other fields up or down: the preview follows.</div>
          <FieldsEditor fields={fields} onChange={(v) => set('fields', v)} />
          <label style={label}>Labels
            <select style={input} value={draft.labelStyle || 'inside'} onChange={(e) => set('labelStyle', e.target.value)}>
              <option value="inside">Inside the boxes (placeholder text)</option>
              <option value="above">Above the boxes</option>
            </select>
          </label>
        </>
      ) },
      { id: 'consents', title: 'Consent boxes', summary: consents.length ? consents.map((c) => c.title || CHANNEL_LABEL[c.channel]).join(' · ') : 'None yet: add one', content: (
        <>
          <div style={{ ...hint, marginTop: 8 }}>Never pre-ticked; people must tick at least one. Their order here is their order on the form.</div>
          <ConsentsEditor consents={consents} onChange={(v) => set('consents', v)} hasMobile={fields.some((x) => x.key === 'mobile')} />
        </>
      ) },
      { id: 'privacy', title: 'Privacy', summary: draft.privacyNotice || draft.privacyUrl ? `Notice${draft.privacyUrl ? ' + policy link' : ''}` : 'No privacy notice', content: (
        <>
          {text('privacyNotice', 'Privacy notice (shown above the button)', 'By clicking below you agree your information will be processed in line with our {privacy}.', { area: true, max: 600 })}
          <div style={{ ...hint, marginTop: -4 }}>Write <code>{'{privacy}'}</code> where the privacy-policy link should go. Leave it out and the link goes at the end.</div>
          {text('privacyUrl', 'Privacy policy link', 'https://…', { max: 500 })}
        </>
      ) },
      { id: 'after', title: 'After they sign up', summary: draft.doubleOptIn ? 'Confirmation email first (double opt-in)' : 'Joins the list straight away', content: (
        <>
          <label style={{ ...label, ...chkRow, marginTop: 8 }}><input type="checkbox" style={chk} checked={!!draft.doubleOptIn} onChange={(e) => set('doubleOptIn', e.target.checked)} /> Ask them to confirm by email first (double opt-in, recommended and expected in the EU)</label>
          <div style={{ ...hint, marginTop: -4 }}>
            {draft.doubleOptIn
              ? 'They get an email with a confirm button. Only people who confirm join the list and its segments. Anyone who doesn’t confirm within 30 days is deleted.'
              : 'People join the list as soon as they submit the form.'}
          </div>
          {text('successMessage', 'Message on the website after they submit', draft.doubleOptIn ? 'Almost there!\nCheck your inbox and tap the link in our email to confirm your subscription.' : 'Thank you!\nYou have successfully joined our subscriber list.', { area: true, max: 300 })}
          {draft.doubleOptIn && (
            <>
              {text('confirmSubject', 'Confirmation email: subject', 'Please confirm your subscription to …', { max: 160 })}
              {text('confirmBody', 'Confirmation email: message', 'Thanks for signing up. Tap the button below to confirm your subscription. If you didn’t sign up, ignore this email.', { area: true, max: 1000 })}
              {text('confirmButton', 'Confirmation email: button', 'Confirm subscription', { max: 40 })}
              {text('confirmedMessage', 'Thank-you page after they confirm', 'Thank you!\nYou have successfully joined our subscriber list.', { area: true, max: 300 })}
            </>
          )}
          <div style={{ ...hint, marginTop: -4 }}>The first line of a message shows in bold. The email uses your branding (logo, colours and sender name).</div>
        </>
      ) },
      { id: 'style', title: 'Style', summary: `${st.labelStyle === 'caps' && st.buttonStyle === 'caps' ? 'Outlined, uppercase labels' : Object.keys(st).length > 3 ? 'Custom look' : 'Pulse default look'}${draft.brandColor ? ` · button ${draft.brandColor}` : ''}`, content: (
        <>
          <label style={label}>Button colour
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" style={{ width: 48, height: 40, border: '1px solid var(--hairline)', borderRadius: 9, background: 'var(--bg)', padding: 2 }}
                value={/^#[0-9a-f]{6}$/i.test(draft.brandColor || '') ? draft.brandColor : '#111111'} onChange={(e) => set('brandColor', e.target.value)} />
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{draft.brandColor ? draft.brandColor : 'Your branding colour'}</span>
              {draft.brandColor && <button type="button" style={{ ...btn, minHeight: 36 }} onClick={() => set('brandColor', '')}>Use branding</button>}
            </div>
          </label>
          <div style={label}>Look</div>
          <StyleSettings kind="newsletter" style={st} onChange={(v) => set('style', v)} onPreset={(p) => setDraft((d) => ({ ...d, style: p.style, brandColor: p.brandColor }))} />
        </>
      ) },
      { id: 'popup', title: 'Popup version', summary: popupWhen(draft.popup || BLANK.popup), content: (
        <PopupSettings popup={draft.popup || BLANK.popup} onChange={(v) => set('popup', v)} />
      ) },
      { id: 'languages', title: 'Languages', summary: [LANGS[draft.defaultLang || 'en'], ...langs.map((l) => LANGS[l] || l)].join(', '), content: (
        <LanguagesEditor draft={draft} setDraft={setDraft} entityId={entityId} apiScope={apiScope} />
      ) },
      { id: 'security', title: 'Allowed websites', summary: draft.allowedDomains || 'Any website', content: (
        <>
          {text('allowedDomains', 'Only accept sign-ups from these websites (optional)', 'e.g. myfestival.co.za, tickets.myfestival.co.za', { max: 1000 })}
          <div style={{ ...hint, marginTop: -4 }}>Leave blank to accept sign-ups from any website. Each domain also covers its subdomains. The hosted link always works.</div>
        </>
      ) },
    ]} />
  );
}

function FieldsEditor({ fields, onChange }) {
  const upd = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, d) => { const a = [...fields]; const [x] = a.splice(i, 1); a.splice(i + d, 0, x); onChange(a); };
  const used = new Set(fields.map((f) => f.key));
  const add = (p) => {
    if (!p) return;
    const key = p.custom ? `${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${fields.length + 1}` : p.key;
    onChange([...fields, { key, label: p.custom && p.label.startsWith('Custom') ? '' : p.label, type: p.type, required: false, options: [] }]);
  };
  return (
    <div>
      {fields.length === 0 && <div style={hint}>Only email is asked. Add a field below if you need more.</div>}
      {fields.map((f, i) => (
        <div key={f.key} style={subRow}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input style={{ ...input, flex: '1 1 160px' }} value={f.label} placeholder="Label people see" maxLength={80} onChange={(e) => upd(i, { label: e.target.value })} aria-label="Field label" />
            <span style={pill}>{f.key === 'country' ? 'Country list' : f.type === 'select' ? 'Dropdown' : f.type === 'tel' ? 'Mobile' : 'Text'}</span>
          </div>
          {f.type === 'select' && f.key !== 'country' && (
            <textarea style={{ ...input, minHeight: 60, marginTop: 6 }} placeholder="One option per line" value={Array.isArray(f.options) ? f.options.join('\n') : f.options || ''}
              onChange={(e) => upd(i, { options: e.target.value.split('\n') })} aria-label="Dropdown options" />
          )}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <label style={{ ...chkRow, minHeight: 36, margin: 0 }}><input type="checkbox" style={chk} checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Required</label>
            <span style={{ flex: 1 }} />
            <button type="button" style={miniBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
            <button type="button" style={miniBtn} disabled={i === fields.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
            <button type="button" style={miniBtn} onClick={() => onChange(fields.filter((_, j) => j !== i))}>Remove</button>
          </div>
        </div>
      ))}
      <select style={{ ...input, marginTop: 8 }} value="" onChange={(e) => add(FIELD_PRESETS[Number(e.target.value)])} aria-label="Add a field">
        <option value="">+ Add a field…</option>
        {FIELD_PRESETS.map((p, i) => (p.custom || !used.has(p.key)) && <option key={i} value={i}>{p.label}</option>)}
      </select>
    </div>
  );
}

function ConsentsEditor({ consents, onChange, hasMobile }) {
  const upd = (i, patch) => onChange(consents.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const used = new Set(consents.map((c) => c.channel).filter((c) => c !== 'other'));
  return (
    <div>
      {consents.map((c, i) => (
        <div key={i} style={subRow}>
          <input style={input} value={c.title} placeholder="Title, e.g. Newsletters" maxLength={120} onChange={(e) => upd(i, { title: e.target.value })} aria-label="Consent title" />
          <textarea style={{ ...input, minHeight: 56, marginTop: 6 }} value={c.text} placeholder="What they are agreeing to" maxLength={600} onChange={(e) => upd(i, { text: e.target.value })} aria-label="Consent description" />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <select style={{ ...input, width: 'auto', flex: '1 1 160px' }} value={c.channel} onChange={(e) => upd(i, { channel: e.target.value })} aria-label="What this consent allows">
              {Object.entries(CHANNEL_LABEL).map(([k, v]) => <option key={k} value={k} disabled={k !== c.channel && k !== 'other' && used.has(k)}>Adds them to: {v}</option>)}
            </select>
            <label style={{ ...chkRow, minHeight: 36, margin: 0 }}><input type="checkbox" style={chk} checked={!!c.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Must tick</label>
            <button type="button" style={miniBtn} disabled={i === 0} onClick={() => onChange(moved(consents, i, -1))} aria-label="Move up">↑</button>
            <button type="button" style={miniBtn} disabled={i === consents.length - 1} onClick={() => onChange(moved(consents, i, 1))} aria-label="Move down">↓</button>
            <button type="button" style={miniBtn} onClick={() => onChange(consents.filter((_, j) => j !== i))}>Remove</button>
          </div>
          {c.channel === 'sms' && !hasMobile && <div style={errBox}>SMS consent needs a Mobile field. Add one above.</div>}
        </div>
      ))}
      {consents.length < 5 && (
        <select style={{ ...input, marginTop: 8 }} value="" onChange={(e) => { const p = CONSENT_PRESETS[Number(e.target.value)]; if (p) onChange([...consents, { key: p.channel, ...p, required: false }]); }} aria-label="Add a consent box">
          <option value="">+ Add a consent box…</option>
          {CONSENT_PRESETS.map((p, i) => (p.channel === 'other' || !used.has(p.channel)) && <option key={i} value={i}>{p.title} ({CHANNEL_LABEL[p.channel]})</option>)}
        </select>
      )}
    </div>
  );
}

// Languages a form can be offered in — mirrors LANGS in server/newsletterI18n.js.
export const LANGS = {
  en: 'English', it: 'Italian', fr: 'French', de: 'German', es: 'Spanish', pt: 'Portuguese', nl: 'Dutch', af: 'Afrikaans',
  zu: 'Zulu', xh: 'Xhosa', st: 'Sotho', tn: 'Tswana', sw: 'Swahili', ar: 'Arabic', pl: 'Polish', ro: 'Romanian', el: 'Greek',
  sv: 'Swedish', da: 'Danish', no: 'Norwegian', fi: 'Finnish', cs: 'Czech', hu: 'Hungarian', hr: 'Croatian', tr: 'Turkish',
  ru: 'Russian', uk: 'Ukrainian', he: 'Hebrew', hi: 'Hindi', ja: 'Japanese', ko: 'Korean', zh: 'Chinese',
};
const T_KEYS = [
  ['heading', 'Heading'], ['blurb', 'Intro line', true], ['buttonLabel', 'Button'], ['successMessage', 'Message after they submit', true],
  ['privacyNotice', 'Privacy notice', true], ['confirmSubject', 'Confirmation email: subject'], ['confirmBody', 'Confirmation email: message', true],
  ['confirmButton', 'Confirmation email: button'], ['confirmedMessage', 'Thank-you page', true],
];

// The form is written in its main language; each translation is shown to fans
// whose browser prefers that language. The Owl drafts a translation from the
// form as it is in this editor; a person reads it and Save keeps it.
function LanguagesEditor({ draft, setDraft, entityId, apiScope }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState('');
  const tr = draft.translations || {};
  const main = draft.defaultLang || 'en';
  const setTr = (lang, v) => setDraft((d) => ({ ...d, translations: { ...(d.translations || {}), [lang]: v } }));
  const translate = async (lang) => {
    if (Object.keys(tr[lang] || {}).length && !window.confirm(`Replace the ${LANGS[lang]} wording with a fresh translation from the Owl?`)) return;
    setBusy(lang); setErr('');
    try {
      const r = await api.newsletterTranslate(entityId, apiScope, { to: lang, form: draft });
      if (r.error) setErr(r.error); else { setTr(lang, r.translation || {}); setOpen(lang); }
    } catch (e) { setErr(String(e.message || e)); }
    setBusy('');
  };
  const add = (lang) => { if (!lang) return; setTr(lang, {}); translate(lang); };
  const remove = (lang) => setDraft((d) => { const t = { ...(d.translations || {}) }; delete t[lang]; return { ...d, translations: t }; });
  return (
    <div>
      <label style={{ ...label, marginTop: 0, fontWeight: 400 }}>Main language (the wording above is written in this)
        <select style={input} value={main} onChange={(e) => { const v = e.target.value; setDraft((d) => { const t = { ...(d.translations || {}) }; delete t[v]; return { ...d, defaultLang: v, translations: t }; }); }}>
          {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>
      <div style={hint}>
        Each fan sees the form in their browser’s language when you have added it here, otherwise in the main language. Their
        confirmation email and thank-you page use the same language, and Language is a filter on the segment, so you can
        send each group a campaign in their own language.
      </div>
      {Object.keys(tr).map((lang) => (
        <div key={lang} style={subRow}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <b style={{ flex: '1 1 120px' }}>{LANGS[lang] || lang}</b>
            <button type="button" style={miniBtn} disabled={!!busy} onClick={() => translate(lang)}>{busy === lang ? 'Translating…' : 'Translate with the Owl'}</button>
            <button type="button" style={miniBtn} onClick={() => setOpen(open === lang ? '' : lang)}>{open === lang ? 'Close' : 'Review'}</button>
            <button type="button" style={miniBtn} onClick={() => remove(lang)}>Remove</button>
          </div>
          {open === lang && <TranslationFields draft={draft} t={tr[lang] || {}} onChange={(v) => setTr(lang, v)} />}
        </div>
      ))}
      <select style={{ ...input, marginTop: 8 }} value="" disabled={!!busy} onChange={(e) => add(e.target.value)} aria-label="Add a language">
        <option value="">{busy ? 'Translating…' : '+ Add a language (the Owl translates it for you to check)…'}</option>
        {Object.entries(LANGS).filter(([k]) => k !== main && !tr[k]).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {err && <div style={errBox}>{err}</div>}
    </div>
  );
}

function TranslationFields({ draft, t, onChange }) {
  const set = (k, v) => onChange({ ...t, [k]: v });
  const setIn = (group, key, v) => onChange({ ...t, [group]: { ...(t[group] || {}), [key]: v } });
  const box = (lbl, value, onSet, { area, ph } = {}) => (
    <label style={{ ...label, fontWeight: 600 }}>{lbl}
      {area ? <textarea style={{ ...input, minHeight: 56 }} value={value || ''} placeholder={ph || ''} onChange={(e) => onSet(e.target.value)} />
        : <input style={input} value={value || ''} placeholder={ph || ''} onChange={(e) => onSet(e.target.value)} />}
    </label>
  );
  const keys = T_KEYS.filter(([k]) => (k !== 'heading' || draft.showHeading !== false) && (!k.startsWith('confirm') || draft.doubleOptIn));
  return (
    <div>
      <div style={{ ...hint, marginTop: 6 }}>The grey text in each box is your main-language wording. A blank box falls back to it.</div>
      {keys.map(([k, lbl, area]) => <div key={k}>{box(lbl, t[k], (v) => set(k, v), { area, ph: draft[k] || '' })}</div>)}
      {(draft.fields || []).map((f) => (
        <div key={f.key}>
          {box(`Field: ${f.label}`, (t.fields || {})[f.key], (v) => setIn('fields', f.key, v), { ph: f.label })}
          {f.type === 'select' && f.key !== 'country' && box(`Field: ${f.label} — options, one per line`, ((t.options || {})[f.key] || []).join('\n'), (v) => setIn('options', f.key, v.split('\n')), { area: true, ph: (f.options || []).join('\n') })}
        </div>
      ))}
      {(draft.consents || []).map((c) => (
        <div key={c.key}>
          {box(`Consent: ${c.title || c.channel} — title`, ((t.consents || {})[c.key] || {}).title, (v) => setIn('consents', c.key, { ...((t.consents || {})[c.key] || {}), title: v }), { ph: c.title })}
          {box(`Consent: ${c.title || c.channel} — description`, ((t.consents || {})[c.key] || {}).text, (v) => setIn('consents', c.key, { ...((t.consents || {})[c.key] || {}), text: v }), { area: true, ph: c.text })}
        </div>
      ))}
      <div style={hint}>Country names are translated automatically.</div>
    </div>
  );
}

// When the popup version opens (only used by the popup embed code).
function PopupSettings({ popup, onChange }) {
  const set = (k, v) => onChange({ ...popup, [k]: v });
  const num = (k, lbl, max, unit) => (
    <label style={{ ...chkRow, minHeight: 40, margin: '6px 0' }}>
      <span style={{ flex: '1 1 180px' }}>{lbl}</span>
      <input type="number" min={0} max={max} inputMode="numeric" style={{ ...input, width: 90, flex: 'none' }} value={popup[k] ?? 0}
        onChange={(e) => set(k, Math.max(0, Math.min(max, Number(e.target.value) || 0)))} aria-label={lbl} />
      <span style={{ width: 56, color: 'var(--muted)' }}>{unit}</span>
    </label>
  );
  return (
    <div style={subRow}>
      <div style={{ ...hint, margin: 0 }}>Used when the form goes on the site as a popup (see “Add it to your website”). Set a number to 0 to turn that trigger off.</div>
      {num('delay', 'Open after', 600, 'seconds')}
      {num('scroll', 'Open once they have scrolled', 100, '% of page')}
      <label style={{ ...chkRow, margin: '6px 0' }}><input type="checkbox" style={chk} checked={!!popup.exit} onChange={(e) => set('exit', e.target.checked)} /> Open when they move to leave the page (computers only)</label>
      {num('days', 'After someone closes it, don’t show it again for', 365, 'days')}
      <div style={{ ...hint, margin: 0 }}>Someone who signs up never sees it again.</div>
    </div>
  );
}

function CodePanel({ form }) {
  const [copied, setCopied] = useState('');
  const [preview, setPreview] = useState('link');
  const copy = (k, v) => { try { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(''), 1500); } catch { /* select manually */ } };
  const CopyBtn = ({ k, value, children }) => <button type="button" style={btn} onClick={() => copy(k, value)}>{copied === k ? '✓ Copied' : children}</button>;
  const linkSnippet = form.embedSnippet.replace('></howler-newsletter>', ' popup="manual"></howler-newsletter>');
  const popupSnippet = form.embedSnippet.replace('></howler-newsletter>', ' popup></howler-newsletter>');
  const iframe = `<iframe src="${form.hostedUrl}?embed=1" style="width:100%;max-width:480px;height:760px;border:0" title="Newsletter sign-up"></iframe>`;
  const previewSrc = preview === 'link' ? `${form.hostedUrl}?link=1` : form.hostedUrl;
  return (
    <div style={panel}>
      <div style={hint}>Pick one. Each works on any site that lets you add HTML (WordPress Custom HTML, Webflow Embed, Wix “Embed code”, Squarespace “Code block”).</div>

      <div style={label}>1 · Open it from a button or text already on your site (recommended)</div>
      <div style={hint}><b>Step 1.</b> Paste this once on the page (or in the site-wide footer/header code). It shows nothing until it is opened.</div>
      <pre style={code}>{linkSnippet}</pre>
      <CopyBtn k="link" value={linkSnippet}>Copy code</CopyBtn>
      <div style={{ ...hint, marginTop: 8 }}>
        <b>Step 2.</b> Set the link of your existing button or text to <code>#newsletter</code>, the same way you would link it to any other page.
        Clicking it opens the form over the page (on a phone it slides up from the bottom). Sharing <code>yoursite.com/#newsletter</code> opens it
        straight away too.
      </div>
      <div style={{ ...hint, marginTop: 4 }}>
        Already using <code>#newsletter</code> for something else? Add <code>link="#signup"</code> to the code above and link to <code>#signup</code> instead.
      </div>

      <div style={label}>2 · Or put the form straight in the page</div>
      <pre style={code}>{form.embedSnippet}</pre>
      <CopyBtn k="embed" value={form.embedSnippet}>Copy code</CopyBtn>
      <div style={{ ...hint, marginTop: 6 }}>Add <code>theme="dark"</code> for dark backgrounds or <code>compact</code> for a one-line footer box.</div>

      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 12.5, cursor: 'pointer', minHeight: 32 }}>3 · Or let it pop up by itself</summary>
        <pre style={{ ...code, marginTop: 6 }}>{popupSnippet}</pre>
        <CopyBtn k="popup" value={popupSnippet}>Copy code</CopyBtn>
        <div style={{ ...hint, marginTop: 6 }}>Opens {popupWhen(form.popup)}. Change when under Edit → Popup version. Links to <code>#newsletter</code> open it as well.</div>
      </details>

      <div style={label}>4 · Or share a link to its own page (Instagram bio, QR code, email)</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ ...input, flex: '1 1 220px' }} readOnly value={form.hostedUrl} onFocus={(e) => e.target.select()} />
        <CopyBtn k="hosted" value={form.hostedUrl}>Copy link</CopyBtn>
        <a style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={form.hostedUrl} target="_blank" rel="noopener noreferrer">Open ↗</a>
      </div>
      <div style={{ ...hint, marginTop: 6 }}>Site builder only allows an iframe? Use this:</div>
      <pre style={code}>{iframe}</pre>
      <CopyBtn k="iframe" value={iframe}>Copy iframe</CopyBtn>

      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 12.5, cursor: 'pointer', minHeight: 32 }}>For developers: build your own form</summary>
        <div style={{ ...hint, marginTop: 6 }}>
          POST JSON to <code>{form.apiUrl}</code> with <code>{'{ email, first_name?, last_name?, mobile?, country?, consents: { email: true }, lang?, page? }'}</code>.
          It returns <code>{'{ ok: true }'}</code> or a 4xx with a readable <code>error</code>. GET the same URL for the form’s wording and fields.
          Leave a hidden <code>website</code> field empty; bots that fill it in are ignored. To open the popup from script, call <code>document.querySelector('howler-newsletter').open()</code>.
        </div>
      </details>

      <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        Preview
        <select style={{ ...input, width: 'auto', minHeight: 36, padding: '4px 8px' }} value={preview} onChange={(e) => setPreview(e.target.value)} aria-label="Which version to preview">
          <option value="link">A link that opens it (tap the link)</option>
          <option value="inline">The form in the page</option>
        </select>
      </div>
      <iframe key={previewSrc} title="Sign-up form preview" src={previewSrc} style={{ width: '100%', maxWidth: 480, height: 760, border: '1px solid var(--hairline)', borderRadius: 12, background: '#fff' }} />
      <div style={{ ...hint, marginTop: 4 }}>Heads up: this preview is live, so anything you submit here is saved as a real sign-up.</div>
    </div>
  );
}

function PeoplePanel({ form, entityId, apiScope }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      api.newsletterSignups(entityId, apiScope, form.id, q).then((v) => setRows(v.signups || [])).catch((e) => setErr(String(e.message || e)));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [entityId, apiScope, form.id, q]);
  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from this list? Use this for a typo, a test or someone who asked to be forgotten.`)) return;
    try { const v = await api.newsletterSignupRemove(entityId, apiScope, form.id, email); setRows(v.signups || []); } catch (e) { setErr(String(e.message || e)); }
  };
  return (
    <div style={panel}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input style={{ ...input, flex: '1 1 200px' }} placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} />
        <a style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }} href={api.newsletterSignupsCsvUrl(entityId, apiScope, form.id)}>⬇ Download CSV</a>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {!rows ? <div style={{ ...hint, marginTop: 8 }}>Loading…</div> : rows.length === 0 ? (
        <div style={{ ...hint, marginTop: 8 }}>{q ? 'No one matches that search.' : 'No sign-ups yet. Once the form is on your website, people appear here as they join.'}</div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
          <table style={table}>
            <thead><tr><th style={th}>Person</th><th style={th}>Signed up</th><th style={th}>Where</th><th style={th} /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.name || r.email}</div>
                    {r.name && <div style={{ color: 'var(--muted)' }}>{r.email}</div>}
                    {r.mobile && <div style={{ color: 'var(--muted)' }}>{r.mobile}</div>}
                    {(r.country || Object.keys(r.answers || {}).length > 0) && <div style={{ color: 'var(--muted)' }}>{[r.country, ...Object.values(r.answers || {})].filter(Boolean).join(' · ')}</div>}
                    <div style={{ color: 'var(--muted)' }}>{[r.emailOk && 'Email ✓', r.smsOk && 'SMS ✓', r.adsOk && 'Ads ✓'].filter(Boolean).join(' · ') || 'No channel consent'}</div>
                    {r.pending && <span style={pillS}>Waiting to confirm</span>}
                    {r.unsubscribed && <span style={{ ...pillS, color: 'var(--error)' }}>Unsubscribed</span>}
                  </td>
                  <td style={td}>{String(r.signedUpAt).slice(0, 10)}</td>
                  <td style={{ ...td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.page}>{hostOf(r.page)}{r.utm?.utm_source ? ` · ${r.utm.utm_source}` : ''}</td>
                  <td style={td}><button type="button" style={{ ...btn, minHeight: 36, padding: '6px 10px' }} onClick={() => remove(r.email)} aria-label={`Remove ${r.email}`}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length >= 500 && <div style={{ ...hint, marginTop: 6 }}>Showing the newest 500. Download the CSV for everyone.</div>}
        </div>
      )}
    </div>
  );
}

const popupWhen = (p = {}) => {
  const parts = [p.delay > 0 && `after ${p.delay} seconds`, p.scroll > 0 && `once ${p.scroll}% of the page is scrolled`, p.exit && 'when they move to leave'].filter(Boolean);
  return parts.length ? parts.join(', or ') : 'never automatically (only from a button)';
};
const hostOf = (u) => { try { return new URL(u).hostname; } catch { return '—'; } };

const hint = { fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' };
const label = { display: 'block', fontSize: 12, fontWeight: 700, margin: '12px 0 4px' };
const chkRow = { display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 400, fontSize: 13, lineHeight: 1.45, minHeight: 32 };
const chk = { width: 18, height: 18, marginTop: 1, flex: 'none' };
const input = { width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 14, fontFamily: 'inherit' };
const btn = { minHeight: 40, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' };
const primary = { background: 'var(--brand, #111)', color: '#fff', borderColor: 'transparent', fontWeight: 700 };
const panel = { marginTop: 12, paddingTop: 4, borderTop: '1px solid var(--hairline)' };
const pill = { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'var(--bg)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
const pillS = { ...pill, display: 'inline-block', marginTop: 3 };
const subRow = { border: '1px solid var(--hairline)', borderRadius: 9, padding: 8, marginTop: 6, background: 'var(--bg)' };
const miniBtn = { ...btn, minHeight: 36, padding: '6px 10px' };
const code = { fontSize: 12, background: 'var(--bg)', border: '1px solid var(--hairline)', borderRadius: 9, padding: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '0 0 8px' };
const errBox = { fontSize: 12.5, color: 'var(--error)', marginTop: 8, lineHeight: 1.5 };
const okBox = { fontSize: 12.5, marginTop: 10, padding: 10, borderRadius: 9, background: 'var(--bg)', border: '1px solid var(--hairline)', lineHeight: 1.5 };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 420 };
const th = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--hairline)', color: 'var(--muted)', fontWeight: 600 };
const td = { padding: '6px 8px', borderBottom: '1px solid var(--hairline)', verticalAlign: 'top' };
